import React from 'react';
import { FormControl, MobileStepper, Theme, makeStyles } from '@material-ui/core'
import * as MuiIcons from '@material-ui/icons';

import { Clients, Fetch, Streams } from '@pitaman71/omniglot-live-data';
import * as Elevated from '@pitaman71/omniglot-introspect';
import * as XLayout from '@pitaman71/react-explicit-layout';

import * as Database from './Database';
import * as Navigation from './Navigation';
import * as Propose from './Propose';

import { Carousel, Conditional, Styler, Views } from '.';

const useStyles = makeStyles((theme) => ({
    dot: {
        backgroundColor: '#808080'
    },
    dotActive: {
        backgroundColor: '#FFFFFF'
    }
}));

export interface Status {
    active: boolean,
    checking: boolean,
    error?: Elevated.JSONValue,
    suggest?: Elevated.JSONValue[]
}

// Low-code model of (one step of a multi-step) conversation where the user negotiates a db change
export interface Question<StreamTypeParams extends Streams.TypeParams = any> {
    key: string, // unique key for this question in a multi-question interview
    stream: Streams.AnyStream<StreamTypeParams>,
    ask: (current: ReturnType<typeof Propose.useStream<StreamTypeParams>>, status: Status) => JSX.Element; // show the data in a format suitable for an interview step (question)
    tell: (current: ReturnType<typeof Propose.useStream<StreamTypeParams>>, status: Status) => JSX.Element; // show the data in a format suitable for the summary page (statement)
    pre?: (current: ReturnType<typeof Propose.useStream<StreamTypeParams>>, status: Status) => string[]; // header/title of the question
    post?: (current: ReturnType<typeof Propose.useStream<StreamTypeParams>>, status: Status) => string[]; // details, additional info about how the data will be stored, shared, used
}

export interface Options {
    suggester?: Suggester,
    theme?: {
        buttons?: Theme,
        card?: Theme
    }
}

export interface Suggester {
    reset: () => void;
    load: (question: string, answer: Elevated.JSONValue) => void;
    check: (question: string, schema?: string) => Promise<{ error?: Elevated.JSONValue, suggest?: Elevated.JSONValue[] }>;
}

function _Buttons(props: { 
    numQuesstions: number,
    slug: string,
    index: number,
    on: {
        cancel: () => Promise<any>,
        next: () => Promise<any>,
        prev: () => Promise<any>,
        commit: () => Promise<any>
    },
    label: {
        checkingAuth: () => JSX.Element,
        failedAuth: () => JSX.Element,
        cancel: () => JSX.Element,
        confirm: () => JSX.Element
    }
}) {
    return (
        <Views.OfControls.AsRow controls={[
            {
                class: Views.OfControls.Direction.Backward,
                id: props.index === 0 ? `${props.slug}-cancel` : `${props.slug}-back`,
                label: () => props.index === 0 ? props.label.cancel() : 'Back',
                icon: () => props.index === 0 ? <MuiIcons.CancelOutlined/> : <MuiIcons.ArrowLeftOutlined/>,
                onClick: props.index === 0 ? props.on.cancel : () => props.on.prev()
            },{
                class: Views.OfControls.Direction.Forward,
                id: props.index === props.numQuesstions ? `${props.slug}-confirm` : `${props.slug}-next`,
                label: () => props.index === props.numQuesstions ? props.label.confirm() : 'Next',
                icon: () => props.index === props.numQuesstions ? <MuiIcons.CheckOutlined/> : <MuiIcons.ArrowRightOutlined/>,
                onClick: props.index === props.numQuesstions ? props.on.commit : () => props.on.next()
            }
        ]} render={({ key, control, busy } ) => <Views.OfControls.AsButton key={key} control={control} variant={control.class === Views.OfControls.Direction.Backward ? 'outlined' : control.class === Views.OfControls.Direction.Forward ? 'contained' : undefined } busy={busy} style={{ flex: "1", whiteSpace: 'nowrap', fontSize: '0.875em' }} />} />
    )
};        

function _Ask<StreamTypeParams extends Streams.TypeParams>(props: {
    numQuesstions: number,
    question: Question<StreamTypeParams>,
    index: number,
    status: Status,
    on: {
        cancel: () => Promise<any>,
        next: () => Promise<any>,
        prev: () => Promise<any>,
        commit: () => Promise<any>
    },
    label: {
        checkingAuth: () => JSX.Element,
        failedAuth: () => JSX.Element,
        cancel: () => JSX.Element,
        confirm: () => JSX.Element
    },
    options: Options
}) {
    const question = props.question;
    const current = Propose.useStream<StreamTypeParams>(props.question.stream);

    return (
        <Styler.Card prefix={question.key} theme={props.options?.theme?.card} style={{ height: '100%' }}>
            <XLayout.Stack.South style={{ height: '100%' }}>
                <div style={{ margin: '0 2em 2em 2em', flex: '0 0 auto'}}>
                { !question.pre ? <p></p> : <React.Fragment>{ question.pre(current, props.status).map(line => <p><Styler.Heading>{line}</Styler.Heading></p>) }</React.Fragment> }
                </div>
                <XLayout.Stack.South scroll={true} style={{ margin: '0 2em 2em 2em', flex: '1 1 auto', minHeight: '4em', maxHeight: '100%' }}>
                    <FormControl fullWidth={true} style={{ flex: '1' }}>{ question.ask(current, props.status) }</FormControl>
                    { !question.post ? <p></p> : <ul>{ question.post(current, props.status).map(line => <li><Styler.Body align='left'>{line}</Styler.Body></li>) }</ul> }
                </XLayout.Stack.South>
                <div style={{ margin: '0 2em 2em 2em', flex: '0 0 auto'}}>
                    <_Buttons {...props} slug={question.key}/>
                </div>
            </XLayout.Stack.South>
        </Styler.Card>
    )
}

function _Tell<StreamTypeParams extends Streams.TypeParams>(props: {
    question: Question<StreamTypeParams>,
    status: Status,
}) {
    const question = props.question;
    const current = Propose.useStream<StreamTypeParams>(props.question.stream);
    return props.question.tell(current, props.status);
}

export function Wizard(props: {
    language: string,
    questions: Question<any>[],
    on: {
        cancel: () => Promise<any>,
        commit: () => Promise<any>
    },
    label: {
        checkingAuth: () => JSX.Element,
        failedAuth: () => JSX.Element,
        cancel: () => JSX.Element,
        confirm: () => JSX.Element
    },
    allow: {
        view?: ReturnType<typeof Propose.useScalarProperty<boolean, Elevated.Domain<boolean>>>,
        edit?: ReturnType<typeof Propose.useScalarProperty<boolean, Elevated.Domain<boolean>>>
    },
    options: Options
}) {
    const classes = useStyles();
    const [ selected, setSelected ] = React.useState(0);
    const [ status, setStatus ] = React.useState<Map<string, Status>>(new Map());

    const on = { 
        ...props.on, 
        next: () => { onUpdate(selected); setSelected(selected + 1); return Promise.resolve(); },
        prev: () => { setSelected(selected - 1); return Promise.resolve() }
    };

    const onUpdate = (index_: number): Promise<any> => {
        if(index_ + 1 >= props.questions.length) return Promise.resolve();
        if(!props.options.suggester) return Promise.resolve();
        const suggester = props.options.suggester;
        suggester.reset();
        let loaded = false;
        return Promise.all(props.questions.map((question, index) => {
            if(index > index_) return undefined;
            return Fetch.useStream(question.stream).then(({ scalar }) => {
                if(scalar && question.pre) {
                    const cue = question.pre(scalar.value, { active: false, checking: false }).join('; ');
                    const asJSON = scalar.domain.asJSON();
                    if(asJSON) {
                        suggester.load(cue, asJSON.to(scalar.value));
                    } else {
                        suggester.load(cue, JSON.stringify(scalar.value));
                    }
                }
            })
        })).then(() => Promise.all(props.questions.map((question, index) => {
            if(status.get(question.key)?.checking) return;
            if(index > index_ && status.get(question.key)?.checking !== true) {
                setStatus(_status => new Map(_status).set(question.key, { 
                    ..._status.get(question.key), 
                    active: false,
                    checking: true 
                }));
                if(question.pre) {
                    const placeholder = {
                        scalar: question.stream.scalar === undefined ? undefined : { domain: question.stream.scalar.domain, value: undefined, status: {} },
                        set: question.stream.set === undefined ? undefined : { domain: question.stream.set.domain, values: new Set(), status: {} },
                        sequence: question.stream.sequence === undefined ? undefined : { domain: question.stream.sequence.domain, values: [], status: {} },
                        relation: question.stream.relation === undefined ? undefined : { entries: [], status: {} }
                    };
                    const cue = question.pre(placeholder, { active: false, checking: true }).join('; ');
                    return suggester.check(cue, placeholder.scalar?.domain.asSchema()).then(({error, suggest}) => {
                        const client = question.stream.scalar?.stateful();
                        const asJSON = question.stream.scalar?.domain.asJSON();
                        if(client && asJSON && Array.isArray(suggest)) {
                            // if there is one answer with confidence above the threshold, automatically assume it
                            const candidates = suggest.filter(item => !!item && typeof item === 'object' && !Array.isArray(item) && typeof item['confidence'] === 'number' && item?.confidence >= 0.95);
                            if(candidates.length === 1 && asJSON && candidates[0] && typeof candidates[0] === 'object' && !Array.isArray(candidates[0])) {
                                client.assign(asJSON.from(candidates[0]['answer'] || null));
                            }
                        }
                        setStatus(_status => new Map(_status).set(question.key, { 
                            checking: false,
                            active: false,
                            error, 
                            suggest
                        }));
                    }).catch(err => {
                        console.error(`AI inference failed: ${err}`);
                        setStatus(_status => new Map(_status).set(question.key, { 
                            checking: false,
                            active: false
                        }));
                    });
                }
            }
        })));
    };
    return (
        <React.Fragment>
            <Conditional show={props.allow.edit?.status.initializing || props.allow.edit?.value === undefined}>
                <XLayout.Center.Both>
                    <p><Styler.Heading>{props.label.checkingAuth()}</Styler.Heading></p>
                </XLayout.Center.Both>
            </Conditional>
            <Conditional show={props.allow.edit?.value === false}>
                <XLayout.Center.Both>
                    <p><Styler.Heading>{props.label.failedAuth()}</Styler.Heading></p>
                </XLayout.Center.Both>
            </Conditional>
            <Conditional show={props.allow.edit?.value === true}>
                <MobileStepper 
                    variant="dots" 
                    position="static"                            
                    steps={props.questions.length + 1}
                    activeStep={selected}
                    nextButton={<React.Fragment/>} 
                    backButton={<React.Fragment/>}
                    classes={classes}
                />
                <Carousel selected={selected} setSelected={setSelected}>
                    {
                        [ ...props.questions.map((question, index) => <_Ask 
                            numQuesstions={props.questions.length}
                            question={question} 
                            index={index} 
                            status={{
                                checking: false,
                                ...status.get(question.key),
                                active: index === selected
                            }}
                            label={props.label}
                            options={props.options}
                            on={on}
                        />), 
                        <Styler.Card prefix='summary' theme={props.options?.theme?.card} style={{ height: '100%' }}>
                            <XLayout.Stack.South style={{ height: '100%' }}>
                                <XLayout.Stack.South scroll={true} style={{ minHeight: '4em', maxHeight: '100%', flex: '1' }}>
                                { props.questions.map(question => {
                                    return (
                                        <div id={question.key} style={{ margin: '2em', flex: '1', alignContent: 'center' }}>
                                            <Styler.Value>
                                                <_Tell question={question} status={{
                                                    checking: false,
                                                    ...status.get(question.key),
                                                    active: selected === props.questions.length
                                                }}/>
                                            </Styler.Value>
                                        </div>
                                    );
                                }) }
                                </XLayout.Stack.South>
                                <div style={{ width: '100%'}}>
                                    <_Buttons 
                                        {...props} 
                                        numQuesstions={props.questions.length} 
                                        slug="summary" 
                                        index={props.questions.length} 
                                        on={on}
                                    />
                                </div>
                            </XLayout.Stack.South>
                        </Styler.Card>
                        ]
                    }
                </Carousel>
            </Conditional>
        </React.Fragment>
    );    
}

export interface Progress {
    key: string,
    status: () => JSX.Element|JSX.Element[],
    details: () => JSX.Element[],
    options: Views.OfControls.Plugin[]
}

export function Waypoint(props: {
    progress: Progress,
    buttonStyle?: React.CSSProperties
}) {
    const { zone } = React.useContext(Database.Context);
    const navigation = React.useContext(Navigation.Context);

    return <Styler.Card prefix={`${props.progress.key}-card`} style={{ width: '90%', height: '100%', display: 'flex' }}>
        <XLayout.Center.Both>
            <XLayout.Stack.South scroll={true} style={{ minHeight: '50%' }}>
                <Styler.Heading style={{ margin: '0.5em', flex: '1' }}>{props.progress.status()}</Styler.Heading>
                <div style={{ flex: '1', width: '100%' }}>
                {
                    props.progress.details().map(child => 
                        <p>
                            {child}
                        </p>)
                }
                </div>                
                <XLayout.Stack.South style={{ flex: '1', width: '100%' }}>
                {
                    props.progress.options.filter(option=> option.class === Views.OfControls.Direction.Forward).map(option => 
                        <div style={{ margin: '0.5em', display: 'inline-block' }}>
                            <Views.OfControls.AsButton control={option} style={props.buttonStyle}/>
                        </div>
                    )
                }
                {
                    props.progress.options.filter(option=> option.class === Views.OfControls.Direction.Backward).map(option => 
                        <div style={{ margin: '0.5em', display: 'inline-block' }}>
                            <Views.OfControls.AsButton control={option} style={props.buttonStyle}/>
                        </div>
                    )
                }
                {
                    props.progress.options.filter(option=> option.class !== Views.OfControls.Direction.Forward && option.class !== Views.OfControls.Direction.Backward).map(option => 
                        <div style={{ margin: '0.5em', display: 'inline-block' }}>
                            <Views.OfControls.AsButton control={option} style={props.buttonStyle}/>
                        </div>
                    )
                }
                </XLayout.Stack.South>
            </XLayout.Stack.South>
        </XLayout.Center.Both>
    </Styler.Card>
}